import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Alert, Backdrop, Box, Button, CircularProgress, Container, Grid, Typography } from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useSpecialties } from 'data/layout/actions'
import { useIdentity } from 'pages/Identity/Login'
import { fetchCreateJob, fetchUpdateJob, fetchGetJob } from 'src/redux/job'
import { fetchHospitalJobs } from 'src/redux/jobs'
import { fetchGetEmployerUsers } from 'src/redux/users'
import GenericCollapsableSection from 'components/Drawers/GenericComponents/GenericCollapsableSection'
import JobSingleSelectTextField from 'components/Drawers/JobDrawer/JobSingleSelectTextField'
import BasicsSection from './sections/BasicsSection'
import LocationSection from './sections/LocationSection'
import CompensationSection from './sections/CompensationSection'
import VisaSection from './sections/VisaSection'
import DescriptionSection from './sections/DescriptionSection'

const JobFormPage = () => {
  const { id } = useParams()
  const isEdit = !!id
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { specialties, subspecialties, candidateTypes } = useSpecialties()
  const { employerUserId, employerId } = useIdentity()
  const { allowedCountries } = useSelector(state => state.employerUser)
  const { users } = useSelector(state => state.users)
  const { job, loading: isLoadingJob } = useSelector(state => state.job)

  const defaultCountry = allowedCountries?.length === 1 ? allowedCountries[0] : 'UnitedStates'

  const [values, setValues] = useState({
    about: null,
    aboutRaw: null,
    title: null,
    candidateTypeId: null,
    country: defaultCountry,
    city: null,
    state: null,
    practiceType: null,
    salaryType: null,
    specialtyIds: [],
    subspecialtyIds: [],
    visaIds: [],
    loanForgiveness: null,
    pslfCompatible: null,
    area: null,
    academicType: null,
    shiftType: null,
    salaryMin: null,
    salaryMax: null,
    salary: null,
    employerUserId: employerUserId,
  })

  const [formSpecialties, setFormSpecialties] = useState([])
  const [formSubspecialties, setFormSubspecialties] = useState([])
  const [showError, setShowError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [didInitialize, setDidInitialize] = useState(false)

  const candidateTypeOptions = candidateTypes.map(({ id: typeId, name }) => ({ id: typeId, name }))
  const employerUserOptions = users.map(user => ({ id: user.id, name: user.email }))

  useEffect(() => {
    if (employerId) {
      dispatch(fetchGetEmployerUsers())
    }
  }, [dispatch, employerId])

  useEffect(() => {
    if (!isEdit && allowedCountries?.length === 1) {
      setValues(prev => ({ ...prev, country: allowedCountries[0], state: allowedCountries[0] !== 'UnitedStates' ? null : prev.state }))
    }
  }, [isEdit, allowedCountries])

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchGetJob(id))
    }
  }, [dispatch, isEdit, id])

  useEffect(() => {
    if (isEdit && job && job.id && !didInitialize) {
      setValues({
        id: job.id ?? null,
        about: job.about ?? null,
        aboutRaw: job.aboutRaw ?? null,
        title: job.title ?? null,
        candidateTypeId: job.candidateTypeId ?? null,
        country: job.country ?? defaultCountry,
        city: job.city ?? null,
        state: job.state ?? null,
        practiceType: job.practiceType ?? null,
        salaryType: job.salaryType ?? null,
        specialtyIds: job.specialties?.map(specialty => specialty.id) ?? [],
        subspecialtyIds: job.subspecialties?.map(subspecialty => subspecialty.id) ?? [],
        visaIds: job.visas?.map(visa => visa.id) ?? [],
        loanForgiveness: job.loanForgiveness ?? null,
        pslfCompatible: job.pslfCompatible ?? null,
        area: job.area ?? null,
        academicType: job.academicType ?? null,
        shiftType: job.shiftType ?? null,
        salaryMin: job.salaryMin ?? null,
        salaryMax: job.salaryMax ?? null,
        salary: job.salary ?? null,
        employerUserId: job.employerUserId ?? employerUserId,
      })
      setDidInitialize(true)
    }
  }, [isEdit, job, didInitialize, employerUserId])

  const memoizedSpecialties = useMemo(() => specialties, [specialties])
  const memoizedSubspecialties = useMemo(() => subspecialties, [subspecialties])
  const memoizedCandidateTypes = useMemo(() => candidateTypes, [candidateTypes])

  useEffect(() => {
    if (values.candidateTypeId && memoizedSpecialties.length && memoizedCandidateTypes.length) {
      const relevantSpecialties = memoizedSpecialties.filter(s =>
        memoizedCandidateTypes
          .find(ct => ct.id === values.candidateTypeId)
          ?.specialtyIds.includes(s.id),
      )
      setFormSpecialties(relevantSpecialties)
    }
  }, [values.candidateTypeId, memoizedSpecialties, memoizedCandidateTypes])

  useEffect(() => {
    if (values.specialtyIds.length && memoizedSubspecialties.length && formSpecialties.length) {
      const updatedSubspecialties = memoizedSubspecialties.filter(sub =>
        values.specialtyIds.some(id =>
          formSpecialties.some(sp => sp.id === id && sp.subspecialtyIds.includes(sub.id)),
        ),
      )
      setFormSubspecialties(updatedSubspecialties)
    }
  }, [values.specialtyIds, memoizedSubspecialties, formSpecialties])

  const handleFieldChange = useCallback((name, val) => {
    setValues(prev => ({ ...prev, [name]: val }))
  }, [])

  const handleEditorChange = useCallback(({ about, aboutRaw }) => {
    setValues(prev => ({ ...prev, about, aboutRaw }))
  }, [])

  const missingRequiredFields = useMemo(() => {
    const fields = []
    if (!values.title) fields.push('Job Title')
    if (!values.candidateTypeId) fields.push('Profession')
    if (!values.practiceType) fields.push('Practice Type')
    if (!values.city) fields.push('City')
    if (values.country === 'UnitedStates' && !values.state) fields.push('State')
    return fields
  }, [values])

  const areRequiredFieldsFilled = useCallback(
    () => missingRequiredFields.length === 0,
    [missingRequiredFields],
  )

  const handleSubmit = useCallback(() => {
    if (!areRequiredFieldsFilled()) {
      setShowError(true)
      return
    }

    setIsSubmitting(true)

    const jobData = {
      ...values,
      areaType: values.area,
      shiftType: values.candidateTypeId === 2 ? values.shiftType : null,
    }

    const onSuccess = () => {
      dispatch(fetchHospitalJobs({ employerId }))
      navigate('/jobs')
    }

    const onFailure = () => {
      setIsSubmitting(false)
    }

    if (isEdit) {
      dispatch(fetchUpdateJob({ values: jobData, onSuccess, onFailure }))
    } else {
      dispatch(fetchCreateJob({ values: jobData, employerId, onSuccess, onFailure }))
    }
  }, [values, isEdit, areRequiredFieldsFilled, dispatch, employerId, navigate])

  if (isEdit && isLoadingJob && !didInitialize) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'calc(100vh - 56px)', md: '100vh' } }}>
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1.5,
        }}
      >
        <Grid container alignItems='center' justifyContent='space-between'>
          <Grid item>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/jobs')}
              sx={{ textTransform: 'none' }}
            >
              Back to Jobs
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {isSubmitting
                ? (isEdit ? 'Updating...' : 'Posting...')
                : (isEdit ? 'Update Job' : 'Post Job')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        <Backdrop
          open={isSubmitting}
          sx={{
            position: 'absolute',
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <CircularProgress />
        </Backdrop>
        <Container maxWidth='md' sx={{ py: 4 }}>
          {showError && missingRequiredFields.length > 0 && (
            <Alert severity='error' sx={{ mb: 3 }}>
              Please fill in the following required fields: {missingRequiredFields.join(', ')}
            </Alert>
          )}

          <Typography variant='h4' sx={{ mb: 3 }}>
            {isEdit ? 'Edit Job' : 'Create New Job'}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <JobSingleSelectTextField
              label='Recruiter'
              name='employerUserId'
              value={employerUserOptions.length > 0 ? values.employerUserId : ''}
              options={employerUserOptions}
              setState={handleFieldChange}
            />
          </Box>

          <GenericCollapsableSection title='Basics' isOpenByDefault={true}>
            <BasicsSection
              values={values}
              setState={handleFieldChange}
              showError={showError}
              candidateTypeOptions={candidateTypeOptions}
              formSpecialties={formSpecialties}
              formSubspecialties={formSubspecialties}
              setJobAlertData={() => {}}
            />
          </GenericCollapsableSection>

          <GenericCollapsableSection title='Location' isOpenByDefault={true}>
            <LocationSection
              values={values}
              setState={handleFieldChange}
              showError={showError}
              setJobAlertData={() => {}}
              allowedCountries={allowedCountries}
            />
          </GenericCollapsableSection>

          <GenericCollapsableSection title='Compensation & Benefits' isOpenByDefault={true}>
            <CompensationSection
              values={values}
              setState={handleFieldChange}
              showError={showError}
              required={false}
              country={values.country}
            />
          </GenericCollapsableSection>

          <GenericCollapsableSection title='Visa & Sponsorship' isOpenByDefault={false}>
            <VisaSection
              values={values}
              setState={handleFieldChange}
            />
          </GenericCollapsableSection>

          <GenericCollapsableSection title='Description' isOpenByDefault={false}>
            <DescriptionSection
              values={values}
              onEditorChange={handleEditorChange}
            />
          </GenericCollapsableSection>
        </Container>
      </Box>
    </Box>
  )
}

export default JobFormPage
